import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Typography } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';

import * as paths from 'paths';
import { useNotify, useGlobalClient } from 'hooks';
import { Login } from 'Links';
import { ClientLogo, FormikEffect, Layouts, Loaders, ValidatedTextFieldNext } from 'components';
import { getAppImage } from 'helpers';

import { ForgotPasswordSendSchema, IForgotPasswordSendValues } from '../validations';
import { AuthContainerProps } from '../types';
import { useSendForgotPasswordEmailMutation } from 'api/public';

const ForgotPassword: FC<AuthContainerProps> = ({ authData, updateUsername }) => {
  const notify = useNotify();
  const client = useGlobalClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate, isLoading } = useSendForgotPasswordEmailMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      notify.info({ message: t('auth:checkEmailResend') });
      navigate(paths.forgotPasswordSubmit);
    },
  });

  const initialValues: IForgotPasswordSendValues = {
    username: authData.username,
  };

  function handleChange({ username }: IForgotPasswordSendValues) {
    updateUsername(username.toLowerCase());
  }

  function handleSend(values: IForgotPasswordSendValues) {
    mutate({ email: values.username.toLowerCase(), client_id: client?.id ?? null });
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/boat.png', 500)} />;
  }

  function renderMain() {
    return (
      <Fragment>
        <Formik
          key="send"
          onSubmit={handleSend}
          initialValues={initialValues}
          validationSchema={ForgotPasswordSendSchema}
        >
          <Layouts.OnboardingBox component={Form}>
            <FormikEffect onChange={handleChange} delay={100} />
            <Box textAlign="center">
              <ClientLogo client={client} />
            </Box>
            <Typography align="center" paragraph variant="h2">
              {t('auth:signInForgotPassword')}
            </Typography>
            <Box paddingTop={3}>
              <ValidatedTextFieldNext
                enableTrim
                fullWidth
                name="username"
                type="email"
                label={t('auth:clientSelectLabel')}
                placeholder={t('auth:clientSelectPlaceholder')}
              />
            </Box>
            <Box paddingTop={3}>
              <Button
                fullWidth
                type="submit"
                size="large"
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {isLoading ? <Loaders.Standard height={26} /> : t('auth:forgotPasswordSendButton')}
              </Button>
            </Box>
            <Box paddingTop={5} textAlign="center">
              <Link color="textSecondary" component={Login}>
                {t('auth:backToLogin')}
              </Link>
            </Box>
          </Layouts.OnboardingBox>
        </Formik>
      </Fragment>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default ForgotPassword;
